import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { LoadingComponent } from '../../shared/components/loading/loading.component';
import { ConfirmComponent, ConfirmDialogData } from '../../shared/dialogs/confirm/confirm.component';
import { ApiService } from '../../_services/api.service';
import { AuthService } from '../../_services/auth.service';
import { DialogService } from '../../shared/components/dialog/dialog.service';
import { CopilotTemplatePayload, Template } from '../../_interfaces/template';
import { DefaultTemplate } from '../../_interfaces/user-settings.interface';
import { flattenSectionsToInstructions } from '../../_helpers/ai-blueprint-format';
import { AnalyticsService } from '../../_services/analytics.service';

type EditorMode = 'create' | 'edit';

const INSTRUCTIONS_SECTION_KEY = 'instructions';

@Component({
  selector: 'app-ai-blueprint-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    LoadingComponent,
    TranslateModule
  ],
  templateUrl: './ai-blueprint-editor.component.html',
  styleUrl: './ai-blueprint-editor.component.scss',
  host: {
    class: 'block h-full p-4 md:px-10 md:pt-6 md:pb-14'
  }
})
export class AiBlueprintEditorComponent implements OnInit, OnDestroy {
  private readonly analytics = inject(AnalyticsService);

  mode: EditorMode = 'create';
  blueprintId: string | null = null;

  title = '';
  instructions = '';
  isDefault = false;

  private originalTitle = '';
  private originalInstructions = '';

  isLoading = false;
  isSaving = false;
  isTogglingDefault = false;
  isDeleting = false;
  hasError = false;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: DialogService,
    private apiService: ApiService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('id');
        this.blueprintId = id;
        this.mode = id ? 'edit' : 'create';
        if (this.mode === 'edit' && id) {
          this.loadBlueprint(id);
        } else {
          this.resetState();
        }
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasUnsavedChanges(): boolean {
    if (this.mode === 'create') {
      const savedBefore = this.originalTitle.length > 0 || this.originalInstructions.length > 0;
      if (savedBefore) {
        return this.hasChanges();
      }
      return this.title.trim().length > 0 || this.instructions.trim().length > 0;
    }
    return this.hasChanges();
  }

  get isTitleTooLong(): boolean {
    return this.title.length > 100;
  }

  get canSave(): boolean {
    if (this.isSaving || this.isLoading || this.isDeleting) return false;
    if (this.title.trim().length < 3) return false;
    if (this.isTitleTooLong) return false;
    if (this.instructions.trim().length === 0) return false;
    if (this.mode === 'edit' && !this.hasChanges()) return false;
    return true;
  }

  get canDelete(): boolean {
    return this.mode === 'edit' && !!this.blueprintId && !this.isLoading && !this.isSaving && !this.isDeleting;
  }

  private hasChanges(): boolean {
    return this.title !== this.originalTitle || this.instructions !== this.originalInstructions;
  }

  private markAsSaved(): void {
    this.originalTitle = this.title;
    this.originalInstructions = this.instructions;
  }

  private resetState(): void {
    this.title = '';
    this.instructions = '';
    this.isDefault = false;
    this.originalTitle = '';
    this.originalInstructions = '';
    this.hasError = false;
  }

  private async loadBlueprint(id: string): Promise<void> {
    this.isLoading = true;
    this.hasError = false;
    try {
      const template = await this.apiService.getTemplate(id, 'user');
      this.applyTemplate(template);
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 404)) {
        return;
      }
      this.hasError = true;
      this.showSnackbar('Unable to load AI blueprint');
    } finally {
      this.isLoading = false;
    }
  }

  private applyTemplate(template: Template): void {
    this.title = template.title ?? '';
    this.instructions = flattenSectionsToInstructions(template.sections);
    this.isDefault = this.isTemplateDefault(template.id, template.type);
    this.originalTitle = this.title;
    this.originalInstructions = this.instructions;
  }

  private isTemplateDefault(id: number, type: string): boolean {
    const defaults = this.authService.getUser()?.settings?.copilotTemplates ?? [];
    return defaults.some(t => t.id === id && t.type === type);
  }

  async retryLoad(): Promise<void> {
    if (this.blueprintId) {
      await this.loadBlueprint(this.blueprintId);
    }
  }

  async onToggleDefault(): Promise<void> {
    const newValue = !this.isDefault;
    this.isDefault = newValue;

    if (this.mode === 'create') return;

    this.isTogglingDefault = true;
    try {
      await this.persistDefault(Number(this.blueprintId), newValue);
      this.showSnackbar(newValue ? 'AI blueprint set as default' : 'AI blueprint removed as default');
    } catch {
      this.isDefault = !newValue;
      this.showSnackbar('Unable to update default, please try again');
    } finally {
      this.isTogglingDefault = false;
    }
  }

  private async persistDefault(id: number, isDefault: boolean): Promise<void> {
    const current = this.authService.getUser()?.settings?.copilotTemplates ?? [];
    let updated: DefaultTemplate[];

    if (isDefault) {
      updated = [...current, { id, type: 'user' as const }];
    } else {
      updated = current.filter(t => !(t.id === id && t.type === 'user'));
    }

    await this.authService.updateUser({ settings: { copilotTemplates: updated } });
  }

  onDelete(): void {
    if (!this.canDelete) return;

    const data: ConfirmDialogData = {
      icon: 'delete',
      title: this.translate.instant('Delete AI blueprint?'),
      description: this.translate.instant('This action cannot be undone. The AI blueprint will be permanently removed.'),
      cancelText: this.translate.instant('Cancel'),
      confirmText: this.translate.instant('Delete blueprint')
    };

    const dialogRef = this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(
      ConfirmComponent,
      { data, presentation: 'dialog' },
    );
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.executeDelete();
    });
  }

  private async executeDelete(): Promise<void> {
    this.isDeleting = true;
    try {
      await this.apiService.deleteCopilotTemplate(this.blueprintId!);
      this.showSnackbar('AI blueprint deleted');
      this.markAsSaved();
      this.router.navigate(['/ai-blueprints']);
    } catch {
      this.showSnackbar('Unable to delete AI blueprint, please try again');
    } finally {
      this.isDeleting = false;
    }
  }

  onBreadcrumbBack(): void {
    this.analytics.track('breadcrumb_clicked', {
      from_page: 'ai_blueprint_editor',
      to_page: 'ai_blueprints',
    });
  }

  async onSave(): Promise<void> {
    if (!this.canSave) return;

    const payload: CopilotTemplatePayload = {
      title: this.title.trim(),
      sections: {
        [INSTRUCTIONS_SECTION_KEY]: { instructions: this.instructions }
      },
      workflow_trigger: true
    };

    this.isSaving = true;
    try {
      if (this.mode === 'create') {
        const created = await this.apiService.createCopilotTemplate(payload);
        if (this.isDefault) {
          await this.persistDefault(created.id, true);
        }
        this.analytics.track('blueprint_created');
        this.showSnackbar('AI blueprint created');
      } else if (this.blueprintId) {
        await this.apiService.updateCopilotTemplate(this.blueprintId, payload);
        this.showSnackbar('AI blueprint updated');
      }
      this.markAsSaved();
      this.router.navigate(['/ai-blueprints']);
    } catch {
      this.showSnackbar('Unable to save, please try again');
    } finally {
      this.isSaving = false;
    }
  }

  private showSnackbar(key: string): void {
    this.snackBar.open(
      this.translate.instant(key),
      this.translate.instant('Close'),
      { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
    );
  }
}
